import type { QuickCustomerInput } from '../api/partyCommands';

/**
 * "Who is the customer?" — the booking's customer step as two tabs, **Find a customer** and **New
 * customer** (option C, chosen by the owner on 2026-10-15). Finding and adding were on one screen
 * with the search silently copied into the new form; now each is its own tab.
 *
 * <p>
 * The rules both halves need, kept here so a screen cannot word them twice: what a search carries
 * into the New tab when nobody matched, where a result matches what was typed, and what stops the
 * Add button. The server keeps the last word — it refuses a number already on file, naming who has
 * it — so this only says what is plainly not ready.
 * </p>
 */

/** Digits only, with an Indian +91 or a leading 0 taken off — the server's own normalising. */
export function mobileDigits(text: string): string {
  const digits = text.replace(/\D/g, '');

  if (digits.length === 12 && digits.startsWith('91')) {
    return digits.slice(2);
  }

  if (digits.length === 11 && digits.startsWith('0')) {
    return digits.slice(1);
  }

  return digits;
}

/** Whether what was typed in Find is a number rather than a name. */
export const looksLikeMobile = (text: string): boolean => /^\+?[\d\s-]+$/.test(text.trim());

/** What a search that found nobody carries into the New tab: a number fills Mobile, words fill Name. */
export function prefillFromSearch(term: string): { name: string; mobile: string } {
  const typed = term.trim();

  return looksLikeMobile(typed) ? { name: '', mobile: typed } : { name: typed, mobile: '' };
}

export interface Highlighted {
  text: string;
  match: boolean;
}

/**
 * A name or number split where it matches what was typed — case and spaces ignored for a name,
 * spacing ignored for a number — so a result shows why it was found. No match gives the whole text.
 */
export function highlight(text: string, term: string): Highlighted[] {
  const typed = term.trim();

  if (typed === '') {
    return [{ text, match: false }];
  }

  if (looksLikeMobile(typed)) {
    const wanted = typed.replace(/\D/g, '');
    // Walk the text keeping a map from each digit to its place, so spacing in either is ignored.
    const places: number[] = [];

    for (let index = 0; index < text.length; index += 1) {
      if (/\d/.test(text[index] ?? '')) {
        places.push(index);
      }
    }

    const digits = text.replace(/\D/g, '');
    const at = wanted === '' ? -1 : digits.indexOf(wanted);

    if (at < 0) {
      return [{ text, match: false }];
    }

    const start = places[at] ?? 0;
    const end = (places[at + wanted.length - 1] ?? start) + 1;

    return split(text, start, end);
  }

  const at = text.toLowerCase().indexOf(typed.toLowerCase());

  return at < 0 ? [{ text, match: false }] : split(text, at, at + typed.length);
}

function split(text: string, start: number, end: number): Highlighted[] {
  return [
    { text: text.slice(0, start), match: false },
    { text: text.slice(start, end), match: true },
    { text: text.slice(end), match: false },
  ].filter((part) => part.text !== '');
}

export interface NewCustomer {
  name: string;
  mobile: string;
  city: string;
  email: string;
}

/** Why Add is not ready, or null — a name, a 10-digit mobile, and an email only if one is typed. */
export function newCustomerProblem(form: NewCustomer): string | null {
  if (form.name.trim() === '') {
    return 'Type their name.';
  }

  if (mobileDigits(form.mobile).length !== 10) {
    return 'Type their 10-digit mobile number.';
  }

  if (form.email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    return 'That email does not look right.';
  }

  return null;
}

/** The quick-add request: blanks sent as nothing, so the server keeps its own empty. */
export function newCustomerInput(form: NewCustomer): QuickCustomerInput {
  return {
    name: form.name.trim(),
    mobile: mobileDigits(form.mobile),
    city: form.city.trim() || null,
    email: form.email.trim() || null,
  };
}
